import {
    DatePipe, formatDate,
    NgForOf,
    NgIf,
    NgSwitch,
    NgSwitchCase,
    NgTemplateOutlet,
    TitleCasePipe,
    UpperCasePipe
} from '@angular/common';
import {SafeUrl} from '@angular/platform-browser';
import {
    ChangeDetectionStrategy, ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {
    FormsModule,
    NgForm,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterLink } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { FuseAlertComponent, FuseAlertType } from '@fuse/components/alert';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { LanguagesComponent } from 'app/shared/components/languages/languages.component';
import {Subject, takeUntil} from "rxjs";
import {FuseConfirmationService} from "../../../../../../@fuse/services/confirmation";
import {FeatureShowOffComponent} from "../../feature-show-off/feature-show-off-component";
import {AppointmentConfirmationDTO} from "../appointment-confirmation-types";
import {
    CalendarEventResponse,
    CalendarResponseDecoded,
    getReasonTextAndColorClass
} from "../../../../../core/types/calendar.types";
import {DateTimeService} from "../../../../../core/services/date.time.service";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {MatDivider} from "@angular/material/divider";
import {AppointmentConfirmService} from "../appointment.confirm.service";
import {SmmLoaderComponent} from "../../../smm-loader/smm.loader.component";
import {MatTooltip} from "@angular/material/tooltip";
import {DomSanitizer} from "@angular/platform-browser";
import {signOut} from "aws-amplify/auth";

@Component({
    selector: 'auth-sign-in',
    templateUrl: './appointment-confirmation.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: fuseAnimations,
    standalone: true,
    styles: `
        /* --- Container & Background Grid --- */
        .ai-container {
            position: relative;
            top: -120px;
            width: 400px;
            height: 400px;
            display: flex;
            justify-content: center;
            align-items: center;
            cursor: pointer;
        }

        .ai-container::before {
            content: "";
            position: absolute;
            width: 150%;
            height: 150%;
            background-image: linear-gradient(rgba(0, 180, 255, 0.2) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 180, 255, 0.2) 1px, transparent 1px);
            background-size: 50px 50px;
            animation: pan-grid 5s linear infinite;
        }

        /* --- The Core --- */
        .core {
            position: absolute;
            width: 120px; /* Replaced --core-size */
            height: 120px; /* Replaced --core-size */
            border-radius: 50%;
            background: radial-gradient(
                circle,
                #ffffff,
                #d6f9ff
            ); /* Replaced --core-color-2, --core-color-1 */
            box-shadow: 0 0 20px #ffffff,
                /* Replaced --core-color-2 */
            0 0 40px #4b99ff,
                /* Replaced --core-color-1 */
            inset 0 0 15px #fff;
            animation: core-breathe 4s ease-in-out infinite;
            transition: all 0.3s ease;
        }

        /* --- The Rings --- */
        .ring {
            position: absolute;
            border-radius: 50%;
            border: 2px solid transparent;
            border-top-color: rgba(0, 255, 255, 0.7); /* Replaced --ring-color */
            border-bottom-color: rgba(0, 255, 255, 0.7); /* Replaced --ring-color */
            box-shadow: 0 0 15px rgba(0, 255, 255, 0.7); /* Replaced --ring-color */
            animation: rotate 10s linear infinite;
            transition: all 0.5s ease;
        }

        .ring-1 {
            width: 180px;
            height: 180px;
            animation-duration: 12s;
        }

        .ring-2 {
            width: 240px;
            height: 240px;
            animation-direction: reverse;
            animation-duration: 9s;
            border-top-color: transparent;
            border-bottom-color: transparent;
            border-left-color: rgba(0, 255, 255, 0.7); /* Replaced --ring-color */
            border-right-color: rgba(0, 255, 255, 0.7); /* Replaced --ring-color */
        }

        .ring-3 {
            width: 300px;
            height: 300px;
            animation-duration: 15s;
            opacity: 0.5;
        }

        /* --- Particles --- */
        .particles {
            position: absolute;
            width: 100%;
            height: 100%;
        }

        .particle {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 4px;
            height: 4px;
            border-radius: 50%;
            background-color: #ffffff; /* Replaced --particle-color */
            box-shadow: 0 0 10px #ffffff; /* Replaced --particle-color */
            opacity: 0;
        }

        /* Position particles around the outer ring */
        .p1 {
            transform: rotate(30deg) translateX(150px);
        }

        .p2 {
            transform: rotate(60deg) translateX(150px);
        }

        .p3 {
            transform: rotate(90deg) translateX(150px);
        }

        .p4 {
            transform: rotate(120deg) translateX(150px);
        }

        .p5 {
            transform: rotate(150deg) translateX(150px);
        }

        .p6 {
            transform: rotate(180deg) translateX(150px);
        }

        .p7 {
            transform: rotate(210deg) translateX(150px);
        }

        .p8 {
            transform: rotate(240deg) translateX(150px);
        }

        .p9 {
            transform: rotate(270deg) translateX(150px);
        }

        .p10 {
            transform: rotate(300deg) translateX(150px);
        }

        .p11 {
            transform: rotate(330deg) translateX(150px);
        }

        .p12 {
            transform: rotate(360deg) translateX(150px);
        }

        /* --- INTERACTION STATES --- */

        /* Listening (Hover) */
        .ai-container:hover .core {
            transform: scale(1.1);
            box-shadow: 0 0 30px #ffffff,
                /* Replaced --core-color-2 */
            0 0 60px #1ecbff,
                /* Replaced --core-color-1 */
            inset 0 0 20px #fff;
        }

        .ai-container:hover .ring {
            animation-duration: 3s !important;
        }

        .ai-container:hover .ring-3 {
            opacity: 1;
        }

        /* Trigger particle animation on hover */
        .ai-container:hover .particle {
            animation: flow-in 1.5s ease-out infinite;
        }

        .ai-container:hover .p2 {
            animation-delay: 0.1s;
        }

        .ai-container:hover .p3 {
            animation-delay: 0.2s;
        }

        .ai-container:hover .p4 {
            animation-delay: 0.3s;
        }

        .ai-container:hover .p5 {
            animation-delay: 0.4s;
        }

        .ai-container:hover .p6 {
            animation-delay: 0.5s;
        }

        .ai-container:hover .p7 {
            animation-delay: 0.6s;
        }

        .ai-container:hover .p8 {
            animation-delay: 0.7s;
        }

        .ai-container:hover .p9 {
            animation-delay: 0.8s;
        }

        .ai-container:hover .p10 {
            animation-delay: 0.9s;
        }

        .ai-container:hover .p11 {
            animation-delay: 1s;
        }

        .ai-container:hover .p12 {
            animation-delay: 1.1s;
        }

        /* Responding (Active/Click) */
        .shockwave {
            position: absolute;
            width: 100px; /* Replaced --core-size */
            height: 100px; /* Replaced --core-size */
            border-radius: 50%;
            background-color: #fff;
            transform: scale(0);
            opacity: 0.7;
        }

        .ai-container:hover .shockwave {
            animation: expand-shockwave 0.5s ease-out;
        }

        .ai-container:active .shockwave {
            animation: expand-shockwave 0.5s ease-out;
        }

        /* --- ANIMATIONS --- */
        @keyframes rotate {
            from {
                transform: rotate(0deg);
            }
            to {
                transform: rotate(360deg);
            }
        }

        @keyframes core-breathe {
            0%,
            100% {
                transform: scale(1);
            }
            50% {
                transform: scale(1.08);
            }
        }

        @keyframes pan-grid {
            0% {
                transform: translate(0, 0);
            }
            100% {
                transform: translate(50px, 50px);
            }
        }

        @keyframes flow-in {
            0% {
                opacity: 0;
                transform-origin: center;
                /* Using --angle variable locally, which should be fine */
                transform: rotate(var(--angle)) translateX(150px) scale(1);
            }
            50% {
                opacity: 1;
            }
            100% {
                opacity: 0;
                transform-origin: center;
                transform: rotate(var(--angle)) translateX(60px) scale(0.5);
            }
        }

        @keyframes expand-shockwave {
            0% {
                transform: scale(1);
                opacity: 0.7;
            }
            100% {
                transform: scale(4);
                opacity: 0;
            }
        }

    `,
    imports: [
        RouterLink,
        FuseAlertComponent,
        NgIf,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatCheckboxModule,
        MatProgressSpinnerModule,
        TranslocoModule,
        LanguagesComponent,
        FeatureShowOffComponent,
        NgForOf,
        DatePipe,
        MatMenu,
        MatMenuItem,
        MatMenuTrigger,
        MatDivider,
        SmmLoaderComponent,
        NgSwitchCase,
        NgSwitch,
        NgTemplateOutlet,
        MatTooltip,
        TitleCasePipe,
        UpperCasePipe,
    ]
})
export class AppointmentConfirmationComponent implements OnInit, OnDestroy {

    componentDestroyed$: Subject<boolean> = new Subject();
    confirmationDto:AppointmentConfirmationDTO;

    currentStatusDecoded: CalendarResponseDecoded;
    startDate: Date;
    endDate: Date;
    relevantDecodedStatuses: Map<CalendarEventResponse, CalendarResponseDecoded> = new Map();
    relevantStatuses: CalendarEventResponse [] = [
        CalendarEventResponse.USER_DECLINED_FINANCIAL_REASONS,
        CalendarEventResponse.USER_DECLINED_OTHER_DENTIST,
        CalendarEventResponse.USER_DECLINED_TOO_BUSY,
        CalendarEventResponse.USER_DECLINED_DENTAL_ANXIETY,
        CalendarEventResponse.USER_DECLINED_HEALTH_ISSUES,
        CalendarEventResponse.USER_DECLINED_MOVED_AWAY,
        CalendarEventResponse.USER_DECLINED,
    ];

    code:string;
    state: ConfirmationState = ConfirmationState.MISSING_CODE;

    alert: { type: FuseAlertType; message: string } = {
        type: 'success',
        message: '',
    };
    showAlert: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _translocoService: TranslocoService,
        private _fuseConfirmationService: FuseConfirmationService,
        private _dateTimeService: DateTimeService,
        private _cdrf: ChangeDetectorRef,
        private _appConfirmSrv: AppointmentConfirmService,
        private sanitizer: DomSanitizer

    ) {
        this.relevantStatuses.forEach(status => {
            this.relevantDecodedStatuses.set(status, getReasonTextAndColorClass(status));
        });
    }


    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {

    }

    @Input()
    set c(c: string) {
        if(c) {
            this.state = ConfirmationState.LOADING;
            this._cdrf.markForCheck();
            this.code = c;
            this._appConfirmSrv.getConfirmationDto(this.code).subscribe(
                {
                    next: value => {
                        this.confirmationDto = value;
                    // Convert clinicLogo bytes to Blob URL
                    //     if (this.confirmationDto.clinicLogo) {
                    //         console.log(this.confirmationDto.clinicLogo);
                    //         const blob = new Blob([new Uint8Array(this.confirmationDto.clinicLogo)], {type: 'image/png'});
                    //         const objectUrl = URL.createObjectURL(blob);
                    //         this.confirmationDto.clinicLogo = this.sanitizer.sanitize(4, objectUrl) as any;
                    //     }

                        this.currentStatusDecoded = getReasonTextAndColorClass(this.confirmationDto.currentResponse);
                        if (this.confirmationDto.startTime) {
                            this.startDate = this._dateTimeService.getDateAndTimeFromBEInDateObject(this.confirmationDto.startTime);
                        }
                        if (this.confirmationDto.endTime) {
                            this.endDate = this._dateTimeService.getDateAndTimeFromBEInDateObject(this.confirmationDto.endTime);
                        }
                        this.state = ConfirmationState.LOADED;
                        this._cdrf.markForCheck();
                    },
                    error: err => {
                        this.state = ConfirmationState.EXPIRED_INVALID_CODE;
                        this._cdrf.markForCheck();
                    },
                    complete: () => {

                    }
                }
            );



        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------


    ngOnDestroy() {
        this.componentDestroyed$.next(true)
        this.componentDestroyed$.complete()
    }

    get currentYear(): number {
        return new Date().getFullYear();
    }


    getIconForDecodedStatus() {
        switch (this.currentStatusDecoded.status) {
            case'Neconfirmat':
            case'Unconfirmed': return 'heroicons_outline:question-mark-circle';
            case'Neprezentare':
            case'No-show': return 'heroicons_outline:hand-thumb-down';
            case'Anulat':
            case'Cancelled': return 'heroicons_outline:x-circle';
            case'Prezent':
            case'Present': return 'heroicons_outline:hand-thumb-up';
            case'Confirmat':
            case'Confirmed': return 'heroicons_outline:check-circle';
            default: return 'heroicons_outline:question-mark-circle';
        }
    }

    getColorForDecodedStatus() {
        switch (this.currentStatusDecoded.status) {
            case'Neconfirmat':
            case'Unconfirmed': return '';
            case'Neprezentare':
            case'No-show': return 'warn';
            case'Anulat':
            case'Cancelled': return 'warn';
            case'Prezent':
            case'Present': return 'primary';
            case'Confirmat':
            case'Confirmed': return 'primary';
            default: return '';
        }
    }

    confirmAppointment(){
        this._appConfirmSrv.confirmAppointment(this.code).pipe(takeUntil(this.componentDestroyed$)).subscribe(
            {
                next: value => {
                    this.state = ConfirmationState.CONFIRMED;
                    this._cdrf.markForCheck();
                    this.showSmmReviewOffer();
                },
                error: err => {
                    this.setAlert('error', 'Error', 'Something went wrong when trying to send your confirmation');
                },
                complete: () => {
                }
            }
        )
    }

    cancelAppointment(status: CalendarEventResponse){
        this._appConfirmSrv.cancelAppointment(this.code, status).pipe(takeUntil(this.componentDestroyed$)).subscribe(
            {
                next: value => {
                    this.state = ConfirmationState.DECLINED;
                    this._cdrf.markForCheck();
                    this.showSmmReviewOffer();
                },
                error: err => {
                    this.setAlert('error', 'Error', 'Something went wrong when trying to send your cancellation');
                },
                complete: () => {
                }
            }
        )
    }

    showSmmReviewOffer(){
        const maxLaterCounts: number = 3;
        const reqInfo:ReviewReqData = JSON.parse(localStorage.getItem('reviewReq')) ?? new ReviewReqData();
        const cancelBut= reqInfo.smmReviewReqInfo.rejectedCounter > maxLaterCounts-1 ? "Never ask again" : "Not this time";
        if(reqInfo.smmReviewReqInfo.acceptedCounter == 0 && !reqInfo.smmReviewReqInfo.neverAsk) {
            this._fuseConfirmationService.showConfirmDialog(
                "Add review?",
                "Would you like to add a review for our informatic system? Be aware that the review is only for informatic system, not for clinic or medical act.",
                "Yes",
                cancelBut,
                () => {
                    reqInfo.smmReviewReqInfo.acceptedCounter++;
                    reqInfo.smmReviewReqInfo.lastAcceptedDate = new Date();
                    localStorage.setItem('reviewReq', JSON.stringify(reqInfo));
                    window.open('https://g.page/r/CcxSmV0h1unqEAE/review', '_blank');
                },
                () => {
                    if(cancelBut == "Never ask again"){
                        reqInfo.smmReviewReqInfo.neverAsk = true;
                    }
                    reqInfo.smmReviewReqInfo.rejectedCounter++;
                    reqInfo.smmReviewReqInfo.lastRejectedDate = new Date();
                    localStorage.setItem('reviewReq', JSON.stringify(reqInfo));
                },
            )
        }
    }


    private setAlert(alertType: FuseAlertType, msg: string, secMsg?:string): void {
        // Set the alert
        let mainMsgTranslation:string = this._translocoService.translate(msg);
        this.alert = {
            type: alertType,
            message: (typeof secMsg !== "undefined") ?  mainMsgTranslation  +' '+  this._translocoService.translate(secMsg)
                :
                mainMsgTranslation
        }
        this.showAlert = true;
        this._cdrf.markForCheck();
    }

    protected readonly ConfirmationState = ConfirmationState;

    closeWindow() {
        window.close();
        // Fallback for browsers that block window.close()
        setTimeout(() => {
            if(this.confirmationDto?.urlWebsite){
                window.location.href = this.confirmationDto?.urlWebsite;
            }else{
                window.location.href = 'https://smilesmanager.ro';
            }
        }, 500);
    }

    refresh() {
        window.location.reload();
    }

    getGoogleCalendarLink() {
        // Datele evenimentului
        const clinicName=this.confirmationDto?.clinicName?this.confirmationDto?.clinicName:'';
        const clinicNumber=this.confirmationDto?.clinicPhoneNumber?this.confirmationDto?.clinicPhoneNumber:'';
        const location = this.confirmationDto?.clinicAddress?this.confirmationDto?.clinicAddress:'';
        const title = "Programare stomatologie. "+clinicName;
        const description = "Programarea dvs. la clinica "+clinicName+".\nPentru mai multe informații accesați "+ window.location.href+" sau apelati "+clinicNumber;
        const start = formatDate(this.startDate, 'yyyyMMddTHHmm00', 'ro-RO'); // "20260623T140000";
        const end = formatDate(this.endDate, 'yyyyMMddTHHmm00', 'ro-RO');


        // Codificarea parametrilor pentru a evita problemele cu spatiile si simbolul $
        const encodedTitle:string = encodeURIComponent(title);
        const encodedDescription:string = encodeURIComponent(description);
        const encodedLocation:string = encodeURIComponent(location);


        // Construirea URL-ului final
                https://calendar.google.com/calendar/render?action=TEMPLATE&text=Example+Google+Calendar+Event&details=More+help+see:+https://support.google.com/calendar/thread/81344786&dates=20201231T160000/20201231T170000&recur=RRULE:FREQ%3DWEEKLY;UNTIL%3D20210603&ctz=America/Toronto
        return 'https://calendar.google.com/calendar/render?action=TEMPLATE&text='+encodedTitle+'&details='+encodedDescription+'&dates='+start+'%2F'+end+'&ctz=Europe%2FBucharest';
    }

    downloadEventIcal() {
        const clinicName = this.confirmationDto?.clinicName ? this.confirmationDto?.clinicName : '';
        const clinicNumber = this.confirmationDto?.clinicPhoneNumber ? this.confirmationDto?.clinicPhoneNumber : '';
        const location = this.confirmationDto?.clinicAddress ? this.confirmationDto?.clinicAddress : '';
        const title = "Programare stomatologie. " + clinicName;
        const description = "Programarea dvs. la clinica " + clinicName + ".\nPentru mai multe informații accesați " + window.location.href + " sau apelati " + clinicNumber;

        const start = formatDate(this.startDate, 'yyyyMMddTHHmm00', 'ro-RO');
        const end = formatDate(this.endDate, 'yyyyMMddTHHmm00', 'ro-RO');
        const now = formatDate(new Date(), 'yyyyMMddTHHmmss', 'en-US') + 'Z';
        const uid = (this.code || Math.random().toString(36).substring(2)) + '@smilesmanager.com';

        const icsContent = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'PRODID:-//Smiles Manager//NONSGML v1.0//EN',
            'CALSCALE:GREGORIAN',
            'BEGIN:VEVENT',
            `DTSTAMP:${now}`,
            `UID:${uid}`,
            `DTSTART:${start}`,
            `DTEND:${end}`,
            `SUMMARY:${title}`,
            `DESCRIPTION:${description.replace(/\n/g, '\\n')}`,
            `LOCATION:${location}`,
            'STATUS:CONFIRMED',
            'END:VEVENT',
            'END:VCALENDAR'
        ].join('\r\n');

        const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'programare-stomatologie.ics');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

}

enum ConfirmationState {
    LOADING="LOADING",
    LOADED="LOADED",
    MISSING_CODE="MISSING_CODE",
    EXPIRED_INVALID_CODE="EXPIRED_INVALID_CODE",
    CONFIRMED="CONFIRMED",
    DECLINED="DECLINED"
}

class ReviewReqInfo {
    acceptedCounter: number = 0;
    lastAcceptedDate: Date = null;
    rejectedCounter: number = 0;
    lastRejectedDate: Date = null;
    neverAsk: boolean = false;
}

class ReviewReqData {
    smmReviewReqInfo: ReviewReqInfo = new ReviewReqInfo();
    clinicReviewReqInfo: ReviewReqInfo = new ReviewReqInfo();
}
